<template>
  <SimpleTextPopup
    :title="$t('details')"
    v-model="value"
    @popupClose="$emit('input', false)"
    :hideRgtBtn="true"
    :medium="(transaction.transactionType === 'fee' && transaction.subtype !== 'other service') ||
      (['label-invoice', 'adjust', 'label'].includes(transaction.type) && !!transaction.details)"
    :small="
      transaction.transactionType === 'deposit' ||
      transaction.subtype === 'other service' ||
      transaction.type === 'deposit'
    ">
    <template v-slot:input>
      <v-layout v-if="['inbound', 'outbound'].includes(transaction.subtype) && transaction.note !== 'custom'">
        <v-tooltip top v-if="isOldVersionData">
          <template v-slot:activator="tooltip">
            <v-flex v-on="tooltip.on" md3 >
              <v-text-field
                :label="$t('packageFee')"
                readonly
                outline
                class="thinBox"
                v-model="transaction.packageFee"
              ></v-text-field>
            </v-flex>
          </template>
          {{ $t('inboundPackageFeeDetail') }}
        </v-tooltip>
        <v-flex md2></v-flex>
        <v-flex md3>
          <v-text-field
            :label="$t('discountRate')"
            readonly
            outline
            class="thinBox"
            :value="`${transaction.discountRate}%`"
          ></v-text-field>
        </v-flex>
        <v-flex md2></v-flex>
        <v-flex md3>
          <v-text-field
            :label="$t('totalPackageQuantity')"
            readonly
            outline
            class="thinBox"
            v-model="transaction.packageQty"
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout
        align-center
        justify-start
        v-if="transaction.otherServices && transaction.otherServices.length || transaction.warehouseNote">
        <v-flex sm3 md2 xl1>{{ $t('otherServices') }}: </v-flex>
        <v-flex><v-chip v-for="item in transaction.otherServices" :key="item">
          {{ (otherServices.find(service => service.value === item)||{}).displayName || item }}
        </v-chip></v-flex>
      </v-layout>
      <v-data-table
        v-if="transaction.subtype === 'inbound' || (transaction.subtype === 'outbound' && !transaction.billingDetails)"
        :headers="headers"
        :items="transaction.items || transaction.products"
        class="elevation-1"
      >
        <template v-slot:items="{item}">
          <td class="subheading" v-if="transaction.subtype === 'inbound'">{{ item.trackings.join(', ') }}</td>
          <td class="text-xs-left">{{ item.upc }}</td>
          <td class="text-xs-left" v-if="!isOldVersionData">{{ item.packageFee }}</td>
          <td class="text-xs-left">{{ item.quantity }}</td>
          <td class="text-xs-left">{{ item.unitFee || transaction.unitFee  || 'N/A'}}</td>
        </template>
      </v-data-table>
      <v-data-table
        v-if="transaction.subtype === 'outbound' && transaction.billingDetails"
        :headers="outboundDetailsHeaders"
        :items="Object.entries(transaction.billingDetails).map(([key, item]) => ({...item, key}))"
        class="elevation-1"
      >
        <template v-slot:items="{item}">
          <td class="text-xs-left">{{ item.key }}</td>
          <td class="text-xs-left">{{ item.quantity }}</td>
          <td class="text-xs-left">{{ item.unit || 0}}</td>
          <td class="text-xs-left">{{ item.total || 0}}</td>
        </template>
      </v-data-table>
      <v-layout v-if="transaction.subtype === 'storage'">
        <v-flex md2>
          <v-text-field
            :label="$t('discountRate')"
            readonly
            outline
            class="thinBox"
            :value="`${transaction.discountRate}%`"
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-data-table
        v-if="transaction.subtype === 'storage'"
        :headers="headers"
        :items="transaction.upcs"
        class="elevation-1"
      >
      <template v-if="transaction.note === 'STORAGE_FEE_IN_INVENTORY_AGE'" v-slot:items="{item}">
        <td class="subheading">{{ item.upc }}</td>
        <td class="text-xs-left">{{ item.unitFee }}</td>
        <td class="text-xs-left">{{ item.quantity }}</td>
        <td class="text-xs-left">{{ toMoney(item.inventoryAge, 3) }}</td>
        <td class="text-xs-left">{{ toMoney(item.billingWeight, 3) }}</td>
        <td class="text-xs-left">{{ toMoney(item.amount, 3) }}</td>
      </template>
        <template v-else-if="transaction.note === 'autoConfirm'" v-slot:items="{item}">
          <td class="subheading">{{ item.upc }}</td>
          <td class="text-xs-left">{{ item.trackings.join(',') }}</td>
          <td class="text-xs-left">{{ toTimestampString(item.inboundTime) }}</td>
          <td class="text-xs-left">{{ item.quantity }}</td>
          <td class="text-xs-left">{{ toMoney(item.amount, 3) }}</td>
        </template>
        <template v-else v-slot:items="{item}">
          <td class="subheading">{{ item.upc }}</td>
          <td class="text-xs-left">{{ item.unitFee }}</td>
          <td class="text-xs-left">{{ item.quantity }}</td>
          <td class="text-xs-left">{{ toMoney(item.amount, 3) }}</td>
        </template>
      </v-data-table>
      <v-container fluid v-if="transaction.transactionType === 'deposit' || transaction.type === 'deposit'">
        <v-layout>
          <v-flex xs8 md6 lg5 xl4>{{ $t('deposit') }}: </v-flex>
          <v-flex xs4>$ {{toMoney(transaction.originDepositAmount || transaction.amount)}}</v-flex>
        </v-layout>
        <v-layout v-if="transaction.bonuses && transaction.bonuses.details.length > 0">
          <v-flex xs8 md6 lg5 xl4>{{ $t('bonus') }}: </v-flex>
          <v-flex xs4>{{transaction.bonuses.details.map(item => `$ ${item.bonus}`).join(' + ')}}</v-flex>
        </v-layout>
        <v-layout>
          <v-flex xs8 md6 lg5 xl4>{{ $t('transactionFee') }}: </v-flex>
          <v-flex xs4>$ {{ transaction.transactionFee || transaction.fee || 0}}</v-flex>
        </v-layout>
        <v-layout v-if="transaction.last4">
          <v-flex xs8 md6 lg5 xl4>{{ $t('cardNumber') }}: </v-flex>
          <v-flex xs4>{{transaction.last4 && `**** ${transaction.last4}`}}</v-flex>
        </v-layout>
        <v-layout v-if='transaction.cardType'>
          <v-flex xs8 md6 lg5 xl4>{{ $t('cardType') }}: </v-flex>
          <v-flex xs4>{{transaction.cardType}}</v-flex>
        </v-layout>
        <v-layout>
          <v-flex xs8 md6 lg5 xl4>{{ $t('transactionTime') }}: </v-flex>
          <v-flex xs4>{{toTimestampString(transaction.createTime)}}</v-flex>
        </v-layout>
        <v-layout>
          <v-flex xs8 md6 lg5 xl4>{{ $t('receiptEmail') }}: </v-flex>
          <v-flex xs4>{{transaction.receiptEmail || 'Unknown'}}</v-flex>
        </v-layout>
        <v-layout>
          <v-flex xs8 md6 lg5 xl4>{{ $t('status') }}: </v-flex>
          <v-flex xs4>{{transaction.status || 'Success'}}</v-flex>
        </v-layout>
      </v-container>
      <v-container fluid v-if="transaction.subType === 'CreditcardProcessFee' && transaction.type === 'fee'">
        <v-layout justify-center>
          <v-flex md4>{{$t('amount')}}</v-flex>
          <v-flex md4>${{transaction.amount}}</v-flex>
        </v-layout>
        <v-layout justify-center>
          <v-flex md4>{{$t('depositAmount')}}</v-flex>
          <v-flex md4>${{transaction.depositAmount}}</v-flex>
        </v-layout>
        <v-layout justify-center>
          <v-flex md4>{{$t('depositorName')}}</v-flex>
          <v-flex md4>{{transaction.depositorName || '---'}}</v-flex>
        </v-layout>
        <v-layout justify-center>
          <v-flex md4>{{$t('transactionId')}}</v-flex>
          <v-flex md4>{{transaction.transactionKey || '---'}}</v-flex>
        </v-layout>
        <v-layout justify-center>
          <v-flex md4>{{$t('transactionTime')}}</v-flex>
          <v-flex md4>{{transaction.createTime instanceof Date ? toTimestampString(transaction.createTime) : '---'}}</v-flex>
        </v-layout>
        <v-layout justify-center>
          <v-flex md4>{{$t('note')}}</v-flex>
          <v-flex md4>{{transaction.note || '---'}}</v-flex>
        </v-layout>
      </v-container>
      <v-container fluid v-if="transaction.subtype === 'other service'">
        <v-layout>
          <v-flex xs5 md4 lg3 xl2>{{ $t('serviceFee') }}: </v-flex>
          <v-flex>$ {{toMoney(transaction.amount)}}</v-flex>
        </v-layout>
        <v-layout>
          <v-flex xs6 md4 lg3 xl2>{{ $t('note') }}: </v-flex>
          <v-flex xs6 md8 lg9 xl10 style="white-space: pre-wrap; overflow: auto;">{{transaction.note}}</v-flex>
        </v-layout>
          <v-layout row wrap>
            <FsFileChip
              v-for="(file, index) in (transaction.attachments || [])"
              :file="file"
              :key="'file' + index"
              width="100%"
              aspect-ratio="1.7"
              class="ma-2"
            />
          </v-layout>
      </v-container>
      <v-container fluid
        v-if="
          allDetails.length
        ">
        <v-layout>
          <v-flex xs12 md8 lg6 xl4>
            <v-select
              v-if="allDetails.length > 1"
              :items="allDetails"
              item-text="labelKey"
              return-object
              v-model="details"
              :label="$t('labelId')"
            ></v-select>
            <v-text-field
              :label="$t('labelId')"
              readonly
              :value="allDetails[0].labelKey"
              v-else
            />
          </v-flex>
        </v-layout>
        <template v-if="details.requestId">
          <v-layout v-if="details.trackingNumber" justify-center>
            <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('trackingNumber') }}: </v-flex>
            <v-flex xs6 md6 lg5 xl3>{{details.trackingNumber}}</v-flex>
          </v-layout>
          <v-layout v-if="details.invoiceDate" justify-center>
            <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('invoiceDate') }}: </v-flex>
            <v-flex xs6 md6 lg5 xl3>{{details.invoiceDate}}</v-flex>
          </v-layout>
          <v-layout v-if="details.estimatedDelivery" justify-center>
            <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('estimatedDelivery') }}: </v-flex>
            <v-flex xs6 md6 lg5 xl3>{{ toDateYYYYMMDDHHmm(new Date(toLocalDateString(details.estimatedDelivery))) }}</v-flex>
          </v-layout>
          <v-layout v-if="details.invoiceNumber" justify-center>
            <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('invoiceNumberType') }}: </v-flex>
            <v-flex xs6 md6 lg5 xl3>{{details.invoiceNumber}} / {{details.type}}</v-flex>
          </v-layout>
          <v-layout justify-center>
            <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right justify-center>{{ $t('serviceType') }}: </v-flex>
            <v-flex xs6 md6 lg5 xl3>{{details.serviceDescription || details.actualServiceType}}</v-flex>
          </v-layout>
          <v-layout v-if="details.weight" justify-center>
            <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('weight') }}: </v-flex>
            <v-flex xs6 md6 lg5 xl3>{{details.originWeight || details.weight}}{{details.isMeasurementMetric ? 'kg' : 'lbs'}}</v-flex>
          </v-layout>
          <v-layout v-if="details.actualWeight" justify-center>
            <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('actualWeight') }}: </v-flex>
            <v-flex xs6 md6 lg5 xl3>{{details.actualWeight}} lbs</v-flex>
          </v-layout>
          <v-layout v-if="details.billingWeight || details.invoiceWeight" justify-center>
            <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('billingWeight') }}: </v-flex>
            <v-flex xs6 md6 lg5 xl3>{{details.billingWeight || details.invoiceWeight}} lbs</v-flex>
          </v-layout>
          <v-layout justify-center>
            <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('dimensions') }}: </v-flex>
            <v-flex xs6 md6 lg5 xl3>
              {{details.actualLength || details.originLength || details.length || (details.packages && (details.packages[0].originLength || details.packages[0].length))}}{{details.isMeasurementMetric ? 'cm' : '"'}}/
              {{details.actualWidth || details.originWidth || details.width || (details.packages && (details.packages[0].originWidth || details.packages[0].width))}}{{details.isMeasurementMetric ? 'cm' : '"'}}/
              {{details.actualHeight || details.originHeight || details.height || (details.packages && (details.packages[0].originHeight || details.packages[0].height))}}{{details.isMeasurementMetric ? 'cm' : '"'}}
              <v-icon v-if="detailPackages.length" small color="info" style="cursor: pointer" @click="showPackageDetails">info</v-icon>
            </v-flex>
          </v-layout>
          <v-layout v-if="details.from || details.shipper" justify-center>
            <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('shipper') }}: </v-flex>
            <v-flex xs6 md6 lg5 xl3>{{formatContact(details.from || details.shipper)}}</v-flex>
          </v-layout>
          <v-layout v-if="details.to || details.recipient" justify-center>
            <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('recipient') }}: </v-flex>
            <v-flex xs6 md6 lg5 xl3>{{formatContact(details.to || details.recipient || details.originalRecipient)}}</v-flex>
          </v-layout>
          <v-layout v-if="details.zone || details.actualZone" justify-center>
            <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('zone') }}: </v-flex>
            <v-flex xs6 md6 lg5 xl3>{{details.zone || details.actualZone }}</v-flex>
          </v-layout>
          <v-layout v-if="allDetails.length === 1 || details.sequenceNumber === 1" justify-center>
            <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('shipmentWeight') }}: </v-flex>
            <v-flex xs6 md6 lg5 xl3>{{details.shipmentWeight || details.weight || (details.packages && details.packages[0].weight)}} lbs</v-flex>
          </v-layout>
          <v-layout v-if="allDetails.length === 1 || details.sequenceNumber === 1" justify-center>
            <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('shipmentBillingWeight') }}: </v-flex>
            <v-flex xs6 md6 lg5 xl3>{{details.shipmentBillingWeight || details.billingWeight}} lbs</v-flex>
          </v-layout>
          <template v-if="allDetails.length === 1 || details.sequenceNumber === 1">
            <v-layout justify-center>
              <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t(isCancelHandleFee ? 'handleFee' : 'discountedPostageAmount') }}: </v-flex>
              <v-flex xs6 md6 lg5 xl3>${{ postageAmount }}</v-flex>
            </v-layout>
            <v-layout v-if="details.discount" justify-center>
              <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('discountLower') }}: </v-flex>
              <v-flex xs6 md6 lg5 xl3>{{details.discount}}% off</v-flex>
            </v-layout>
            <v-layout :key="name" v-for="(amount, name) in detailSurcharges" justify-center>
              <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('surcharge') }} - {{name}}: </v-flex>
              <v-flex xs6 md6 lg5 xl3>${{amount}}</v-flex>
            </v-layout>
            <v-layout v-if="details.invoiceNumber" justify-center>
              <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('totalAmount') }}: </v-flex>
              <v-flex xs6 md6 lg5 xl3>${{getTotalAmount(details.actualAmountDetails, details.discount)}}</v-flex>
            </v-layout>
          </template>
          <v-layout my-2>
            <hr />
          </v-layout>
          <template v-if="details.sequenceNumber === 1">
            <v-layout v-if="details.invoiceNumber" justify-center>
              <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('difference') }}: </v-flex>
              <v-flex xs6 md6 lg5 xl3>${{-transaction.amount}}</v-flex>
            </v-layout>
            <v-layout v-if="transaction.discount" justify-center>
              <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t('VIPDiscount') }}: </v-flex>
              <v-flex xs6 md6 lg5 xl3>{{transaction.discount}}%</v-flex>
            </v-layout>
            <v-layout v-if="details.orderId" justify-center>
              <v-flex pr-4 xs6 md6 lg5 xl3 text-xs-right>{{ $t(isCancelHandleFee ? 'handleFee' : 'totalAmount') }}: </v-flex>
              <v-flex xs6 md6 lg5 xl3>${{details.totalAmount}}</v-flex>
            </v-layout>
          </template>

          <!-- invoice details -->
          <v-data-table
            v-if="details.invoiceNumber"
            :headers="detailsHeaders"
            :items="details.packages"
            class="elevation-1"
            hide-actions>
            <template v-slot:items="{item}">
              <td class="text-xs-left">
                {{ item.trackingNumber }}
              </td>
              <td class="text-xs-left">
                {{ `${item.length || item.actualLength} x ${item.width || item.actualWidth} x ${item.height || item.actualHeight}` }}
              </td>
              <td class="text-xs-left">
                {{ item.billingWeight || item.weight || item.actualWeight }}
              </td>
              <td class="text-xs-left">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span v-on="on">{{ sumObjVal(item.amountDetails) }}</span>
                  </template>
                  <span>
                    <div v-for="(val, key) in item.amountDetails" :key="key">
                      {{ key }}: {{ val }}
                    </div>
                  </span>
                </v-tooltip>
              </td>
            </template>
          </v-data-table>
        </template>
      </v-container>

      <SimpleTextPopup
        :title="$t('package')"
        v-model="isShowPackageDetails"
        @popupClose="isShowPackageDetails = false"
        hideRgtBtn
      >
        <template v-slot:input>
          <v-data-table
            :headers="[
              { text: $t('id#'), align: 'left', sortable: false, value: 'key' },
              { text: $t('dimension'), value: 'dimension', align: 'left', sortable: false },
              { text: $t('weight'), value: 'weight', align: 'left', sortable: false },
              { text: $t('billingWeight'), value: 'billingWeight', align: 'left', sortable: false },
              { text: $t('qty'), value: 'qty', align: 'left', sortable: false }
            ]"
            :items="detailPackages"
            hide-actions
            class="elevation-1"
          >
            <template v-slot:items="props">
              <td class="subheading">{{ props.index + 1}}</td>
              <td>{{props.item.dimension}}</td>
              <td>{{props.item.weight}} lbs</td>
              <td>{{props.item.billingWeight}} lbs</td>
              <td>{{props.item.qty}}</td>
            </template>
          </v-data-table>
        </template>
      </SimpleTextPopup>
    </template>
  </SimpleTextPopup>
</template>

<script>
import SimpleTextPopup from './SimpleTextPopup'
import FsFileChip from './FsFileChip.vue'
import {decimalTools, timeTools, addNumbers, toMoney, sumObjVal} from '../utils/tools'

export default {
  name: 'BillingTransactionDetail',
  components: {
    SimpleTextPopup,
    FsFileChip
  },
  mixins: [decimalTools, timeTools],
  data () {
    return {
      details: {},
      isShowPackageDetails: false
    }
  },
  computed: {
    postageAmount () {
      const details = this.details.amountDetails || this.details.actualAmountDetails || {}
      const amount = details.postageAmount || 0
      const shpAmount = details['postageAmount (SHP)'] || 0
      return toMoney(amount + shpAmount)
    },
    isCancelHandleFee () {
      return this.transaction.keywords.includes('cancel handle fee')
    },
    warehouse () {
      return this.$store.getters.activeWarehouse
    },
    isOldVersionData () {
      return !(this.transaction.items || []).some(item => item.packageFee)
    },
    outboundDetailsHeaders () {
      if (!this.transaction.billingDetails) return []
      const amountTotal = addNumbers(Object.values(this.transaction.billingDetails).reduce((total, detail) => {
        return addNumbers(total, detail.total)
      }, 0) * (1 - this.transaction.discountRate / 100))
      return [
        { text: this.$t('name'), value: 'key', align: 'left', sortable: false },
        { text: this.$t('quantity'), value: 'quantity', align: 'left', sortable: false },
        { text: this.$t('unitFee'), value: 'unitFee', align: 'left', sortable: false },
        { text: `${this.$t('amount')}(${amountTotal})`, value: 'total', align: 'left', sortable: false }
      ]
    },
    headers () {
      switch (this.transaction.subtype) {
        case 'storage': {
          if (this.transaction.note === 'autoConfirm') {
            return [
              { text: this.$t(this.transaction.isPkgStorageFee ? 'tracking' : 'upc'), value: 'upc', align: 'left', sortable: false },
              { text: this.$t('trackings'), value: 'trackings', align: 'left', sortable: false },
              { text: this.$t('inboundTime'), value: 'inboundTime', align: 'left', sortable: false },
              { text: this.$t('quantity'), value: 'quantity', align: 'left', sortable: false },
              { text: this.$t('amount'), value: 'amount', align: 'left', sortable: false }
            ]
          }
          if (this.transaction.note === 'STORAGE_FEE_IN_INVENTORY_AGE') {
            return [
              { text: this.$t('upc'), value: 'upc', align: 'left', sortable: false },
              { text: this.$t('unitFee'), value: 'unitFee', align: 'left', sortable: false },
              { text: this.$t('quantity'), value: 'quantity', align: 'left', sortable: false },
              { text: this.$t('inventoryAge'), value: 'inventoryAge', align: 'left', sortable: false },
              { text: `${this.$t('billingWeight')}(${this.$t('cubicInch')})`, value: 'billingWeight', align: 'left', sortable: false },
              { text: this.$t('amount'), value: 'amount', align: 'left', sortable: false }
            ]
          }
          return [
            { text: this.$t(this.transaction.isPkgStorageFee ? 'tracking' : 'upc'), value: 'upc', align: 'left', sortable: false },
            { text: this.$t('unitFee'), value: 'unitFee', align: 'left', sortable: false },
            { text: this.$t('quantity'), value: 'quantity', align: 'left', sortable: false },
            { text: this.$t('amount'), value: 'amount', align: 'left', sortable: false }
          ]
        }
        case 'inbound':
          let headers = [
            { text: this.$t('trackings'), value: 'trackings', align: 'left', sortable: false },
            { text: this.$t('upc'), value: 'upc', align: 'left', sortable: false },
            { text: this.$t('quantity'), value: 'quantity', align: 'left', sortable: false },
            { text: this.$t('unitFee'), value: 'unitFee', align: 'left', sortable: false }
          ]
          if (!this.isOldVersionData) headers.splice(2, 0, { text: this.$t('packageFee'), value: 'packageFee', align: 'left', sortable: false })
          return headers
        case 'outbound':
          return [
            { text: this.$t('upc'), value: 'upc', align: 'left', sortable: false },
            { text: this.$t('quantity'), value: 'quantity', align: 'left', sortable: false },
            { text: this.$t('unitFee'), value: 'unitFee', align: 'left', sortable: false }
          ]
        default:
          return []
      }
    },
    detailsHeaders () {
      return [
        { text: this.$t('trackingNumber'), value: 'trackingNumber', align: 'left', sortable: false },
        { text: this.$t('dimensions'), value: 'dimensions', align: 'left', sortable: false },
        { text: this.$t('billingWeight'), value: 'billingWeight', align: 'left', sortable: false },
        { text: this.$t('totalAmount'), value: 'totalAmount', align: 'left', sortable: false }
      ]
    },
    allDetails () {
      return (this.transaction.details || []).map(detail => {
        if (detail.labelKey) return detail
        return {
          labelKey: detail.requestId,
          ...detail
        }
      })
    },
    detailSurcharges () {
      const {postageAmount, ...surcharges} = (this.details.amountDetails || this.details.actualAmountDetails || {})
      return Object.entries(surcharges).reduce((obj, [key, value]) => {
        if (value && key !== 'postageAmount (SHP)') {
          obj[key] = value
        }
        return obj
      }, {})
    },
    detailPackages () {
      const packages = []
      let keyMap = {}
      if (this.details?.packages?.length) {
        for (const pkg of this.details.packages) {
          const {actualLength = 0, actualWidth = 0, actualHeight = 0, actualWeight = 0, billingWeight = 0} = pkg
          const dimension = [actualLength, actualWidth, actualHeight].map(num => `${num}"`).join('x')
          const key = [dimension, `${actualWeight}lbs`].join('-')
          if (keyMap[key] >= 0) {
            packages[keyMap[key]].qty += 1
          } else {
            const idx = Object.keys(keyMap).length
            keyMap[key] = idx
            packages[keyMap[key]] = {
              key,
              dimension,
              weight: actualWeight,
              billingWeight,
              qty: 1
            }
          }
        }
      }
      return packages
    },
    otherServices () {
      return this.$store.getters.otherServices
    }
  },
  methods: {
    formatContact (contactObj) {
      const {name = '', fullName = '', company = '', address1 = '', address2 = '', city = '', state = '', zipCode = ''} = contactObj
      return `${name || fullName}, ${company},` +
        ` ${address1 + (address2 ? (', ' + address2) : '')},` +
        ` ${city}, ${state} ${zipCode}`
    },
    getTotalAmount (amountDetails, discount = 0) {
      const {postageAmount = 0} = amountDetails
      return toMoney(postageAmount * (1 - discount / 100) + this.getTotalSurcharge(amountDetails))
    },
    getTotalSurcharge (amountDetails = {}) {
      const {postageAmount, ...surcharges} = amountDetails
      return Object.values(surcharges).reduce((sum, val) => addNumbers(sum, val || 0), 0)
    },
    showPackageDetails () {
      this.isShowPackageDetails = true
    },
    sumObjVal
  },
  mounted () {
    this.details = this.allDetails[0] || {}
  },
  props: {
    value: Boolean,
    transaction: Object,
    isTenant: Boolean
  }
}
</script>



// WEBPACK FOOTER //
// src/components/BillingTransactionDetail.vue